/**
 * CrazySettings - Option Sanitization, Mutual Exclusivity & Randomization for Crazy Cribbage
 */

import { CRAZY_OPTIONS_DEFS } from './CrazyManager.js';

export class CrazySettings {
  /**
   * Returns a clean map of default settings for Crazy Cribbage options.
   * @returns {Object}
   */
  static getCrazyDefaults() {
    const defaults = {};
    for (const opt of CRAZY_OPTIONS_DEFS) {
      defaults[opt.key] = opt.default;
    }
    return defaults;
  }

  /**
   * Sanitizes Crazy Cribbage settings:
   * - Enforces mutual exclusivity among zone pegging options (Bunkers vs Peg Through vs Last 5 vs Stink Hole).
   * - Enforces mutual exclusivity between Speedo Cribbage and Pick Your Crib.
   * - Disables 2-player-only options (Stolen Crib, Speedo Cribbage) if player count exceeds 2.
   * @param {Object} settings
   * @returns {Object} mutated settings
   */
  static sanitizeCrazyOptions(settings = {}) {
    if (!settings) return settings;

    // Mutual exclusivity for pegging zones
    if (settings.optBunkers) {
      settings.optPegThroughZones = false;
      settings.optLast5PegOut = false;
      settings.optStinkHole = false;
    } else if (settings.optLast5PegOut) {
      settings.optPegThroughZones = false;
      settings.optStinkHole = false;
    } else if (settings.optPegThroughZones) {
      settings.optStinkHole = false;
    }

    // Mutual exclusivity for crib-stealing / hand-passing / draw options / 7-card
    if (settings.opt7CardCribbage) {
      if (settings.optSpeedoCribbage) settings.optSpeedoCribbage = false;
      if (settings.optPickYourCrib) settings.optPickYourCrib = false;
      if (settings.optDrawCribbage) settings.optDrawCribbage = false;
    }
    if (settings.optSpeedoCribbage && settings.optPickYourCrib) {
      settings.optPickYourCrib = false;
    }
    if (settings.optDrawCribbage) {
      if (settings.optSpeedoCribbage) settings.optSpeedoCribbage = false;
      if (settings.optPickYourCrib) settings.optPickYourCrib = false;
      if (settings.optFivesCribbage) settings.optFivesCribbage = false;
      if (settings.optSevensAndEightsCribbage) settings.optSevensAndEightsCribbage = false;
      if (settings.opt7CardCribbage) settings.opt7CardCribbage = false;
    } else if (settings.optSevensAndEightsCribbage) {
      if (settings.optDrawCribbage) settings.optDrawCribbage = false;
      if (settings.optFivesCribbage) settings.optFivesCribbage = false;
      if (settings.optSpeedoCribbage) settings.optSpeedoCribbage = false;
      if (settings.optPickYourCrib) settings.optPickYourCrib = false;
    } else if (settings.optFivesCribbage) {
      if (settings.optDrawCribbage) settings.optDrawCribbage = false;
      if (settings.optSpeedoCribbage) settings.optSpeedoCribbage = false;
      if (settings.optPickYourCrib) settings.optPickYourCrib = false;
    } else if (settings.optSpeedoCribbage || settings.optPickYourCrib) {
      if (settings.opt7CardCribbage) settings.opt7CardCribbage = false;
      settings.optDrawCribbage = false;
      settings.optFivesCribbage = false;
      settings.optSevensAndEightsCribbage = false;
    }

    // Mutual exclusivity for Shotgun Cribbage: dealer does not count crib points,
    // so negative crib (Rock the Crib), zero crib (-19), stolen crib, and twisted cribbage are mutually exclusive.
    if (settings.optShotgunCribbage) {
      if (settings.optRockCrib) settings.optRockCrib = false;
      if (settings.optZeroCrib) settings.optZeroCrib = false;
      if (settings.optStolenCrib) settings.optStolenCrib = false;
      if (settings.optTwistedCribbage) settings.optTwistedCribbage = false;
    }
    if (settings.optTwistedCribbage) {
      if (settings.optShotgunCribbage) settings.optShotgunCribbage = false;
      if (settings.opt7CardCribbage) settings.opt7CardCribbage = false;
    }

    // Options that force a specific player count and mode (e.g. Canadian Doubles)
    const modeForcingOpt = CRAZY_OPTIONS_DEFS.find(opt => opt.requiredPlayerMode && settings[opt.key]);
    if (modeForcingOpt) {
      const req = Array.isArray(modeForcingOpt.requiredPlayers) ? modeForcingOpt.requiredPlayers : [Number(modeForcingOpt.requiredPlayers)];
      settings.playerCount = req[0];
      if (modeForcingOpt.requiredGameMode) {
        settings.gameMode = modeForcingOpt.requiredGameMode;
      }
    }

    // Player count and mode constraints for resulting settings.playerCount
    const pCount = Number(settings.playerCount) || 2;
    const gMode = settings.gameMode || 'individual';
    CRAZY_OPTIONS_DEFS.forEach(opt => {
      if (opt.requiredPlayers != null) {
        const req = Array.isArray(opt.requiredPlayers) ? opt.requiredPlayers : [Number(opt.requiredPlayers)];
        const countAllowed = req.includes(pCount);
        const modeAllowed = !opt.requiredGameMode || opt.requiredGameMode === gMode;
        if (!countAllowed || !modeAllowed) {
          settings[opt.key] = false;
        }
      }
    });

    // Enforce mutual exclusivity among any remaining active restricted options
    const remainingActive = CRAZY_OPTIONS_DEFS.filter(opt => opt.requiredPlayers != null && settings[opt.key]);
    if (remainingActive.length > 1) {
      const primary = remainingActive[0];
      const primaryReq = Array.isArray(primary.requiredPlayers) ? primary.requiredPlayers : [Number(primary.requiredPlayers)];
      remainingActive.slice(1).forEach(other => {
        const otherReq = Array.isArray(other.requiredPlayers) ? other.requiredPlayers : [Number(other.requiredPlayers)];
        const hasOverlap = otherReq.some(c => primaryReq.includes(c));
        const modeCompatible = !primary.requiredGameMode || !other.requiredGameMode || primary.requiredGameMode === other.requiredGameMode;
        if (!hasOverlap || !modeCompatible) {
          settings[other.key] = false;
        }
      });
    }

    return settings;
  }

  /**
   * Randomizes Crazy Cribbage options.
   * @param {Object} [currentSettings]
   * @param {boolean} [isFull=false] If true, higher chance of activating multiple options
   * @returns {Object} Updated options dictionary
   */
  static randomizeCrazyOptions(currentSettings = {}, isFull = false) {
    const next = { ...currentSettings };
    const pCount = Number(next.playerCount) || 2;

    for (const opt of CRAZY_OPTIONS_DEFS) {
      if (opt.requiredPlayers != null) {
        const req = Array.isArray(opt.requiredPlayers) ? opt.requiredPlayers : [Number(opt.requiredPlayers)];
        if (!req.includes(pCount)) {
          next[opt.key] = false;
          continue;
        }
      }
      // Probability depends on isFull (30% vs 65%)
      const prob = isFull ? 0.65 : 0.30;
      next[opt.key] = Math.random() < prob;
    }

    // Sanitize to enforce mutual exclusivity and player count consistency
    CrazySettings.sanitizeCrazyOptions(next);
    return next;
  }
}
